import { useMemo, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ListSurface } from '@/components/layouts';
import {
  DataGrid,
  type DataGridColumn,
  type DataGridSortDir,
} from '@/components/organisms/data-grid';

export type SettingsDataGridSortDir = DataGridSortDir;

export interface SettingsDataGridColumn<TKey extends string = string> {
  key: TKey;
  label: string;
  defaultVisible?: boolean;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  headerClassName?: string;
  cellClassName?: string;
  width?: number;
  /** When set, this column absorbs horizontal slack (AG Grid native flex). If no
   *  column declares flex, the grid auto-flexes the first column — which for grids
   *  whose first column is a fixed checkbox is wrong, so set flex on a data column. */
  flex?: number;
  wrap?: boolean;
}

interface SettingsDataGridPagination {
  count: number;
  singular: string;
  plural?: string;
  page: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  variant?: 'data-table' | 'entity-list';
  pageSize?: number;
}

interface SettingsDataGridProps<TRow, TKey extends string> {
  columns: SettingsDataGridColumn<TKey>[];
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  renderCell: (key: TKey, row: TRow, column: SettingsDataGridColumn<TKey>) => ReactNode;
  onVisibleColumnsChange?: (columns: SettingsDataGridColumn<TKey>[]) => void;
  storageKey: string;
  emptyIcon?: LucideIcon;
  emptyTitle: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  showClearAll: boolean;
  onClearAll: () => void;
  activeFilterCount: number;
  filterSheet?: {
    title?: string;
    content: ReactNode;
    onReset: () => void;
    resetDisabled: boolean;
    /** Commit the drafted filters (deferred-apply, NOT-391). Runs before close. */
    onApply?: () => void;
    /** Reseed the draft from the applied filters when the drawer opens (NOT-391). */
    onOpen?: () => void;
  };
  toolbarActions?: ReactNode;
  exporting?: boolean;
  onExportXLSX?: () => void;
  onExportCSV?: () => void;
  showExport?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  errorTitle?: string;
  onRetry?: () => void;
  skeletonRowCount?: number;
  onRowClick?: (row: TRow) => void;
  isFullWidthRow?: (row: TRow) => boolean;
  renderFullWidthRow?: (row: TRow) => ReactNode;
  getRowHeight?: (row: TRow) => number | undefined;
  rowClassName?: string | ((row: TRow) => string);
  renderRowActions?: (row: TRow) => ReactNode;
  actionCellClassName?: string;
  sortKey?: TKey | null;
  sortDir?: DataGridSortDir | null;
  onSort?: (column: SettingsDataGridColumn<TKey>, dir: DataGridSortDir) => void;
  pagination?: SettingsDataGridPagination;
  fixedColumnWidths?: boolean;
  className?: string;
  rowHeight?: number;
}

export function SettingsDataGrid<TRow, TKey extends string>(
  props: SettingsDataGridProps<TRow, TKey>,
) {
  const columnByKey = useMemo(() => {
    return new Map(props.columns.map((column) => [column.key, column]));
  }, [props.columns]);

  const gridColumns = useMemo<DataGridColumn<TKey>[]>(() => {
    return props.columns.map((column) => ({
      key: column.key,
      label: column.label,
      sortable: column.sortable,
      align: column.align,
      width: column.width,
      flex: column.flex,
      hide: column.defaultVisible === false,
    }));
  }, [props.columns]);

  function getColumn(key: TKey) {
    const column = columnByKey.get(key);
    if (!column) {
      throw new Error(`Unknown settings grid column: ${key}`);
    }
    return column;
  }

  return (
    <ListSurface className={props.className}>
      <DataGrid
        columns={gridColumns}
        rows={props.rows}
        getRowKey={props.getRowKey}
        renderCell={(key, row) => props.renderCell(key, row, getColumn(key))}
        renderRowActions={props.renderRowActions}
        actionsColumnWidth={props.renderRowActions ? 48 : undefined}
        onRowClick={props.onRowClick}
        isFullWidthRow={props.isFullWidthRow}
        renderFullWidthRow={props.renderFullWidthRow}
        getRowHeight={props.getRowHeight}
        storageKey={props.storageKey}
        emptyTitle={props.emptyTitle}
        isLoading={props.isLoading}
        isError={props.isError}
        errorTitle={props.errorTitle}
        onRetry={props.onRetry}
        searchValue={props.searchValue}
        onSearchChange={props.onSearchChange}
        searchPlaceholder={props.searchPlaceholder}
        showClearAll={props.showClearAll}
        onClearAll={props.onClearAll}
        activeFilterCount={props.activeFilterCount}
        toolbarActions={props.toolbarActions}
        filterSheet={props.filterSheet}
        sortKey={props.sortKey}
        sortDir={props.sortDir}
        onSortChange={(key, dir) => {
          if (!key) return;
          props.onSort?.(getColumn(key), dir ?? 'asc');
        }}
        sortingOrder={['asc', 'desc']}
        pagination={props.pagination}
        rowHeight={props.rowHeight ?? 56}
        persistenceVersion="settings-v3"
        onVisibleColumnsChange={
          props.onVisibleColumnsChange
            ? (columns) =>
                props.onVisibleColumnsChange?.(columns.map((column) => getColumn(column.key)))
            : undefined
        }
      />
    </ListSurface>
  );
}
